import React, { useEffect, useRef } from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { srConfig } from '@config'; // Ensure this file exists and exports a valid configuration object
import sr from '@utils/sr'; // Ensure this file exists and exports a valid ScrollReveal instance
import { usePrefersReducedMotion } from '@hooks';

const StyledAboutSection = styled.section`
  // max-width: 900px; /* Standardized max width */
  margin: 0 auto; /* Center align the section */
  padding: 20px 50px; /* Standardized padding */
  text-align: left; /* Changed alignment to left */

  @media (max-width: 768px) {
    padding: 60px 30px; /* Adjusted for smaller screens */
    font-size: var(--fz-md); /* Adjusted font size for smaller screens */
    max-width: 100%; /* Full width for smaller screens */
  }
  @media (max-width: 480px) {
    padding: 10px 20px; /* Adjusted for mobile screens */
    font-size: var(--fz-sm); /* Further adjusted font size for mobile */
    max-width: 100%; /* Full width for mobile screens */
  }

  @media (max-width: 300px) {
    padding: 5px 10px; /* Further adjusted for very small screens */
    font-size: var(--fz-xs); /* Further adjusted font size for very small screens */
    max-width: 100%; /* Full width for very small screens */
  }

  .inner {
    display: grid;
    grid-template-columns: 2fr 1fr; /* Adjusted to better manage space */
    grid-gap: 50px;

    @media (max-width: 768px) {
      display: block;
    }
  }
`;

// Updated text alignment to avoid awkward spaces
const StyledText = styled.p`
  text-align: left; /* Changed from justify to left to avoid awkward spaces */
  font-size: var(--fz-md);
  font-family: var(--font-mono);
  color: var(--light-slate);
`;

const StyledSkillsList = styled.ul`
  margin: 0;
  padding-left: 20px;

  li {
    margin-bottom: 5px; /* Matches spacing in jobs */
  }
`;

const StyledPic = styled.div`
  position: relative;
  margin: 0 auto; /* Center the image */
  max-width: 400px; /* Adjusted for better fit on all screens */

  @media (max-width: 768px) {
    margin: 50px auto 0;
    max-width: 300px; /* Adjusted for smaller screens */
  }

  .wrapper {
    ${({ theme }) => theme.mixins.boxShadow};
    display: block;
    position: relative;
    border-radius: var(--border-radius);
    background-color: var(--green);

    &:hover,
    &:focus {
      outline: 0;
      transform: translate(-4px, -4px);

      &:after {
        transform: translate(8px, 8px);
      }

      .img {
        filter: none;
        mix-blend-mode: normal;
      }
    }

    .img {
      position: relative;
      border-radius: var(--border-radius);
      mix-blend-mode: multiply;
      filter: grayscale(100%) contrast(1);
      transition: var(--transition);
    }

    &:before,
    &:after {
      content: '';
      display: block;
      position: absolute;
      width: 100%;
      height: 100%;
      border-radius: var(--border-radius);
      transition: var(--transition);
    }

    &:before {
      top: 0;
      left: 0;
      background-color: var(--navy);
      mix-blend-mode: screen;
    }

    &:after {
      border: 2px solid var(--green);
      top: 14px;
      left: 14px;
      z-index: -1;
    }
  }
`;

const About = () => {
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    if (sr && typeof sr.reveal === 'function') {
      sr.reveal(revealContainer.current, srConfig());
    } else {
      console.error('ScrollReveal is not properly initialized.');
    }
  }, []);

  const skills = [
    {
      category: 'Data & Analytics',
      items: ['Data Engineering', 'Analytics', 'Data Governance', 'DataOps'],
    },
    {
      category: 'Cloud & Infrastructure',
      items: ['AWS', 'Azure', 'Docker', 'Kubernetes', 'DevOps'],
    },
    {
      category: 'AI & ML',
      items: ['Machine Learning', 'Generative AI', 'LLMs', 'MLOps'],
    },
    {
      category: 'Development',
      items: ['Full Stack', 'Cloud Native', 'Microservices', 'API Design'],
    },
    {
      category: 'Teams & Services',
      items: ['Team Augmentation', 'Technical Consulting', 'Project Leadership'],
    },
  ];

  return (
    <StyledAboutSection id="about" ref={revealContainer}>
      <h2 className="numbered-heading">About Us</h2>
      <div className="inner">
        <StyledText>
          <p>
            Draa Talents connects businesses with top-tier IT professionals and consulting services.
            We specialize in delivering innovative tech solutions across data, cloud, AI, and
            beyond.
          </p>
          <p>Our core competencies:</p>
          <StyledSkillsList className="skills-list">
            {skills.map((skill, index) => (
              <li key={index}>
                <strong>{skill.category}:</strong> {skill.items.join(', ')}
              </li>
            ))}
          </StyledSkillsList>
        </StyledText>
        <StyledPic>
          <div className="wrapper">
            <StaticImage
              className="img"
              src="../../images/me.png"
              width={500}
              quality={95}
              formats={['AUTO', 'WEBP', 'PNG']}
              alt="Draa Talents"
            />
          </div>
        </StyledPic>
      </div>
    </StyledAboutSection>
  );
};

export default About;
